import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/mockups/ui";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Target, Pencil, Check, X, Loader2, CheckCircle2, XCircle } from "lucide-react";

const eur = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
const pct = (n) => `${(n ?? 0).toLocaleString("es-ES", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

function ObjetivoEditor({ value, onSaved }) {
  const { user } = useAuth();
  const canEdit = ["Administrador", "Directora"].includes(user?.role);
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const cfg = await api.get("/budget-config");
      await api.put("/budget-config", { ...cfg.data, objetivo_empresa: (Number(val) || 0) / 100 });
      toast.success("Objetivo de empresa actualizado");
      setEditing(false);
      onSaved?.();
    } catch { toast.error("No se pudo guardar"); }
    finally { setSaving(false); }
  };

  return (
    <Card className="border-border shadow-none p-4 flex items-center gap-4 bg-amber-50/60" data-testid="objetivo-card">
      <div className="flex items-center gap-2 text-sm text-muted-foreground"><Target className="h-4 w-4 text-gold" /> Objetivo de empresa</div>
      {editing ? (
        <div className="flex items-center gap-2">
          <Input type="number" step="0.5" value={val} onChange={(e) => setVal(e.target.value)}
                 className="w-24 h-8" data-testid="objetivo-input" />
          <span className="text-sm">%</span>
          <Button size="icon" className="h-8 w-8" onClick={save} disabled={saving} data-testid="objetivo-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          </Button>
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setEditing(false); setVal(value); }}><X className="h-4 w-4" /></Button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <span className="text-xl font-bold text-navy tabular-nums">{pct(value)}</span>
          {canEdit && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing(true)} data-testid="objetivo-edit"><Pencil className="h-3.5 w-3.5" /></Button>}
        </div>
      )}
    </Card>
  );
}

export default function CierresReport() {
  const { data, loading, refetch } = useFetch("/reports/cierres");
  const rows = data?.rows || [];

  return (
    <div className="space-y-6 animate-fade-in-up p-1" data-testid="cierres-report-page">
      <PageHeader title="Cierre de proyectos" subtitle="Rentabilidad de cada proyecto: margen del presupuesto original frente al ejecutado, comparado con el objetivo de empresa." />

      <ObjetivoEditor value={data?.objetivo_empresa ?? 15} onSaved={refetch} />

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>
      ) : rows.length === 0 ? (
        <Card className="border-border shadow-none p-10 text-center text-sm text-muted-foreground">No hay proyectos con presupuesto todavía.</Card>
      ) : (
        <Card className="border-border shadow-none overflow-hidden" data-testid="cierres-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Proyecto</TableHead>
                <TableHead className="text-right">Total s/IVA</TableHead>
                <TableHead className="text-right">Margen original</TableHead>
                <TableHead className="text-right">Margen final</TableHead>
                <TableHead className="text-right">Resultado gestión</TableHead>
                <TableHead className="text-center">Objetivo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => {
                const g = r.resultado_gestion_pct;
                return (
                  <TableRow key={r.project_id} className="group" data-testid={`cierre-row-${r.project_id}`}>
                    <TableCell>
                      <Link to={`/proyecto/${r.project_id}?tab=cierre`} className="font-medium text-navy hover:text-gold transition-colors">
                        {r.reference ? <span className="text-muted-foreground mr-1">{r.reference} ·</span> : null}{r.name}
                      </Link>
                      {r.hours_approx && <span className="ml-1 text-[10px] text-muted-foreground align-super" title="Horas internas estimadas con tarifa media">*</span>}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{eur(r.total)}</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">{pct(r.original_margen_pct)}</TableCell>
                    <TableCell className={cn("text-right tabular-nums font-semibold", r.cumple ? "text-emerald-700" : "text-rose-700")}>{pct(r.final_margen_pct)}</TableCell>
                    <TableCell className={cn("text-right tabular-nums", g >= 0 ? "text-emerald-700" : "text-rose-700")}>{g > 0 ? "+" : ""}{pct(g)}</TableCell>
                    <TableCell className="text-center">
                      {r.cumple
                        ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 gap-1"><CheckCircle2 className="h-3 w-3" />Cumple</Badge>
                        : <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100 gap-1"><XCircle className="h-3 w-3" />No</Badge>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
      {rows.some((r) => r.hours_approx) && (
        <p className="text-xs text-muted-foreground">* Horas internas ejecutadas estimadas con tarifa media (Teamleader no desglosa horas por persona).</p>
      )}
    </div>
  );
}
